// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import {loadCategories} from '@app/features/feed/commands/FeedCategoryCommands';
import {saveFeedInterests} from '@app/features/feed/commands/FeedInterestCommands';
import {FeedCategoryIcon} from '@app/features/feed/components/FeedCategoryIcon';
import styles from '@app/features/feed/components/FeedInterestsModal.module.css';
import {FEED_INTERESTS_DESCRIPTOR} from '@app/features/feed/utils/FeedCategoryMessages';
import {shownFeedCategories} from '@app/features/feed/utils/FeedCategoryUtils';
import {CANCEL_DESCRIPTOR, TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import UserSettings from '@app/features/user/state/UserSettings';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckIcon, SparkleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const PROMPT_TITLE_DESCRIPTOR = msg({
	message: 'What are you into?',
	comment:
		'Title of the small window that asks, once, which feed topics (Football, Valorant, Music...) the user likes. Casual and short.',
});
const EDIT_TITLE_DESCRIPTOR = msg({
	message: 'Your interests',
	comment: 'Title of the window where the user changes the feed topics they picked as interests. Keep it concise.',
});
const DESCRIPTION_DESCRIPTOR = msg({
	message: 'Pick the topics you like and Discover shows you more of them. You can change this anytime.',
	comment: 'Text under the title of the feed interests window. Discover is the feed tab of popular public posts.',
});
const SELECTED_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# picked} other {# picked}}',
	comment: 'How many topics are picked in the feed interests window, beside its buttons. Keep it concise.',
});
export const MY_INTERESTS_DESCRIPTOR = msg({
	message: 'My interests',
	comment:
		'Small button in the Discover feed header and in the settings that opens the feed topics the user picked as interests, to change them. Keep it concise.',
});
const SETTING_DESCRIPTION_DESCRIPTOR = msg({
	message: 'The topics you pick show up more in Discover. Nobody else sees them.',
	comment: 'Text of the feed interests settings section, beside the button that changes them.',
});
const SKIP_DESCRIPTOR = msg({
	message: 'Skip',
	context: 'feed-interests',
	comment:
		'Button in the feed interests window that answers the question without picking anything; it is not asked again. Keep it concise.',
});
const SAVE_DESCRIPTOR = msg({
	message: 'Save',
	context: 'feed-interests',
	comment: 'Button in the feed interests window that saves the picked topics. Keep it concise.',
});

// Micup akis-v3: the feed topics of the user's country as chips to pick from, any number of them. `prompt`: the
// question Discover opens once (Skip saves an empty set and records the answer); otherwise the later edit (Cancel only
// closes). Picks outside the country's list are not shown and are dropped by a save.
export const FeedInterestsModal = observer(({prompt = false}: {prompt?: boolean}) => {
	const {i18n} = useLingui();
	const categories = shownFeedCategories();
	const [picked, setPicked] = useState<ReadonlySet<string>>(
		() => new Set(UserSettings.feedInterestCategoryIds.filter((id) => categories.some((entry) => entry.id === id))),
	);
	const [saving, setSaving] = useState<'save' | 'skip' | null>(null);
	const toggle = (id: string) => {
		setPicked((current) => {
			const next = new Set(current);
			if (!next.delete(id)) next.add(id);
			return next;
		});
	};
	const save = async (action: 'save' | 'skip') => {
		setSaving(action);
		const ids = action === 'skip' ? [] : categories.filter((entry) => picked.has(entry.id)).map((entry) => entry.id);
		const ok = await saveFeedInterests(ids);
		setSaving(null);
		if (ok) ModalCommands.pop();
		else ToastCommands.error(i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
	};
	return (
		<Modal.Root size="small" centered data-flx="feed.feed-interests-modal.modal-root">
			<Modal.Header
				title={i18n._(prompt ? PROMPT_TITLE_DESCRIPTOR : EDIT_TITLE_DESCRIPTOR)}
				data-flx="feed.feed-interests-modal.modal-header"
			/>
			<Modal.Content data-flx="feed.feed-interests-modal.modal-content">
				<Modal.ContentLayout data-flx="feed.feed-interests-modal.modal-content-layout">
					<p className={styles.description} data-flx="feed.feed-interests-modal.description">
						{i18n._(DESCRIPTION_DESCRIPTOR)}
					</p>
					<div className={styles.chips} data-flx="feed.feed-interests-modal.chips">
						{categories.map((category) => {
							const selected = picked.has(category.id);
							return (
								<button
									key={category.id}
									type="button"
									className={clsx(styles.chip, selected && styles.chipSelected)}
									aria-pressed={selected}
									onClick={() => toggle(category.id)}
									data-flx="feed.feed-interests-modal.chip"
								>
									<FeedCategoryIcon
										emoji={category.emoji}
										className={styles.emoji}
										data-flx="feed.feed-interests-modal.emoji"
									/>
									<span className={styles.name} data-flx="feed.feed-interests-modal.name">
										{category.name}
									</span>
									{selected && (
										<CheckIcon
											weight="bold"
											className={styles.check}
											aria-hidden
											data-flx="feed.feed-interests-modal.check"
										/>
									)}
								</button>
							);
						})}
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer className={styles.footer} data-flx="feed.feed-interests-modal.modal-footer">
				<span className={styles.count} aria-live="polite" data-flx="feed.feed-interests-modal.count">
					{i18n._(SELECTED_COUNT_DESCRIPTOR, {count: picked.size})}
				</span>
				{prompt ? (
					<Button
						variant="secondary"
						onClick={() => void save('skip')}
						disabled={saving !== null}
						submitting={saving === 'skip'}
						data-flx="feed.feed-interests-modal.button.skip"
					>
						{i18n._(SKIP_DESCRIPTOR)}
					</Button>
				) : (
					<Button
						variant="secondary"
						onClick={() => ModalCommands.pop()}
						disabled={saving !== null}
						data-flx="feed.feed-interests-modal.button.cancel"
					>
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
				)}
				<Button
					onClick={() => void save('save')}
					disabled={saving !== null}
					submitting={saving === 'save'}
					data-flx="feed.feed-interests-modal.button.save"
				>
					{i18n._(SAVE_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});

export function openFeedInterests(prompt = false): void {
	ModalCommands.push(
		modal(() => <FeedInterestsModal prompt={prompt} data-flx="feed.feed-interests-modal.feed-interests-modal" />),
	);
}

// The "My interests" button in the Discover header: a small pill, an icon on the phone layout.
export const FeedInterestsButton = observer(({className}: {className?: string}) => {
	const {i18n} = useLingui();
	const label = i18n._(MY_INTERESTS_DESCRIPTOR);
	if (MobileLayout.enabled) {
		return (
			<ChannelHeaderIcon
				icon={SparkleIcon}
				label={label}
				className={className}
				aria-haspopup="dialog"
				onClick={() => openFeedInterests()}
				data-flx="feed.feed-interests-modal.feed-interests-button.channel-header-icon"
			/>
		);
	}
	return (
		<Button
			small
			fitContent
			variant="secondary"
			className={clsx(styles.headerButton, className)}
			leftIcon={
				<SparkleIcon weight="fill" aria-hidden data-flx="feed.feed-interests-modal.feed-interests-button.icon" />
			}
			aria-haspopup="dialog"
			onClick={() => openFeedInterests()}
			data-flx="feed.feed-interests-modal.feed-interests-button"
		>
			{label}
		</Button>
	);
});

// The privacy settings section: what the interests do and the same window to change them. Shown only once the server
// keeps interests and the user's country has topics, so no empty section is drawn.
export const FeedInterestsSetting = observer(() => {
	const {i18n} = useLingui();
	useEffect(() => {
		void loadCategories();
	}, []);
	if (UserSettings.feedInterestsAnsweredAt === undefined || shownFeedCategories().length === 0) return null;
	return (
		<SettingsSection
			id="feed-interests"
			tabType="privacy_safety"
			title={i18n._(FEED_INTERESTS_DESCRIPTOR)}
			data-flx="feed.feed-interests-modal.feed-interests-setting.section"
		>
			<div className={styles.setting} data-flx="feed.feed-interests-modal.feed-interests-setting">
				<p className={styles.settingText} data-flx="feed.feed-interests-modal.feed-interests-setting.text">
					{i18n._(SETTING_DESCRIPTION_DESCRIPTOR)}
				</p>
				<Button
					small
					fitContent
					variant="secondary"
					leftIcon={
						<SparkleIcon weight="fill" aria-hidden data-flx="feed.feed-interests-modal.feed-interests-setting.icon" />
					}
					aria-haspopup="dialog"
					onClick={() => openFeedInterests()}
					data-flx="feed.feed-interests-modal.feed-interests-setting.button"
				>
					{i18n._(MY_INTERESTS_DESCRIPTOR)}
				</Button>
			</div>
		</SettingsSection>
	);
});
